/**
 * A bitmap recipe made only of geometry (`place`, `crop`, `render`,
 * `roundedClip`) and `tint`, as one canvas drawing: where the source image
 * lands on the output, which rounded rectangles clip it, and the tint color.
 * The browser then draws it in one `drawImage`, at the output size, instead
 * of src/imageOps.ts running each step over a float raster in JavaScript,
 * which was a quarter of Ice Cubes' main thread at startup (Nuke resizes
 * every avatar with `place` + `render`).
 *
 * The output has the size the raster pipeline gives the same recipe (so the
 * element shows the same resolution); within it, the image is drawn smoothly
 * where the pipeline samples the nearest pixel. Recipes with a filter, or a
 * `clamp` (its edge pixels repeat), are not planned: `planBitmap` answers
 * null and they take the raster pipeline.
 */

import type { BitmapStep } from './protocol';

/** A rectangle in output pixels, rows from the top. */
export interface PixelRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BitmapPlan {
  /** Output size in pixels; 0 × 0 is an empty image. */
  width: number;
  height: number;
  /** Where the whole source image is drawn. */
  image: PixelRect;
  /** Rounded rectangles the drawing is clipped to (corner radii in pixels). */
  clips: (PixelRect & { rx: number; ry: number })[];
  /** The image's shape in one color (each pixel's alpha times `a`), when tinted. */
  tint: { r: number; g: number; b: number; a: number } | null;
}

/** A rectangle in Core Image's space: units of source pixels, origin at the bottom left, y up. */
interface UnitRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * The drawing for `steps` over a `sourceWidth` × `sourceHeight` source at
 * `scale` output pixels per source pixel, or null when a step needs pixels
 * (a filter, `clamp`, an op this module does not know).
 */
export function planBitmap(steps: readonly BitmapStep[], sourceWidth: number, sourceHeight: number, scale: number): BitmapPlan | null {
  // What the raster pipeline tracks: the rectangle the raster covers, its
  // size in pixels and pixels per unit; and where the image and clips are.
  let extent: UnitRect = { x: 0, y: 0, w: sourceWidth, h: sourceHeight };
  let s = scale;
  let width = Math.max(0, Math.round(sourceWidth * s));
  let height = Math.max(0, Math.round(sourceHeight * s));
  let image: UnitRect = { ...extent };
  let clips: (UnitRect & { rx: number; ry: number })[] = [];
  let tint = null as BitmapPlan['tint'];
  const empty: BitmapPlan = { width: 0, height: 0, image: { x: 0, y: 0, width: 0, height: 0 }, clips: [], tint: null };

  for (const step of steps) {
    switch (step.op) {
      case 'place': {
        const { sx, sy, tx, ty } = step;
        if (!(sx > 0) || !(sy > 0)) return empty;
        const move = (r: UnitRect): UnitRect => ({ x: r.x * sx + tx, y: r.y * sy + ty, w: r.w * sx, h: r.h * sy });
        extent = move(extent);
        image = move(image);
        clips = clips.map((c) => ({ ...move(c), rx: c.rx * sx, ry: c.ry * sy }));
        if (Math.abs(sx - sy) <= 1e-9 * Math.max(sx, sy)) {
          // A uniform scale relabels the raster: the same pixels.
          s /= sx;
        } else {
          s /= Math.min(sx, sy);
          width = Math.max(0, Math.round(extent.w * s));
          height = Math.max(0, Math.round(extent.h * s));
        }
        break;
      }
      case 'crop': {
        extent = intersect({ x: step.x, y: step.y, w: step.width, h: step.height }, extent);
        width = Math.max(0, Math.round(extent.w * s));
        height = Math.max(0, Math.round(extent.h * s));
        break;
      }
      case 'render': {
        // The raster covers the rectangle (clear outside the image), then its
        // origin moves to 0, 0: everything shifts by the rectangle's origin.
        const dx = -step.x;
        const dy = -step.y;
        extent = { x: 0, y: 0, w: step.width, h: step.height };
        width = Math.max(0, Math.round(step.width * s));
        height = Math.max(0, Math.round(step.height * s));
        image = { ...image, x: image.x + dx, y: image.y + dy };
        clips = clips.map((c) => ({ ...c, x: c.x + dx, y: c.y + dy }));
        break;
      }
      case 'roundedClip': {
        const rx = Math.max(0, Math.min(step.rx, step.width / 2));
        const ry = Math.max(0, Math.min(step.ry, step.height / 2));
        clips.push({ x: step.x, y: step.y, w: step.width, h: step.height, rx, ry });
        break;
      }
      case 'tint': {
        const c = (v: number) => Math.min(1, Math.max(0, Number.isFinite(v) ? v : 0));
        // A second tint recolors the first's shape: its color, both alphas.
        tint = { r: c(step.r), g: c(step.g), b: c(step.b), a: c(step.a) * (tint?.a ?? 1) };
        break;
      }
      default:
        return null;
    }
  }
  if (width === 0 || height === 0 || !(extent.w > 0) || !(extent.h > 0)) return empty;

  // Units to output pixels: x from the extent's left, y down from its top.
  const kx = width / extent.w;
  const ky = height / extent.h;
  const toPixels = (r: UnitRect): PixelRect => ({
    x: (r.x - extent.x) * kx,
    y: (extent.y + extent.h - (r.y + r.h)) * ky,
    width: r.w * kx,
    height: r.h * ky,
  });
  return {
    width,
    height,
    image: toPixels(image),
    clips: clips.map((c) => ({ ...toPixels(c), rx: c.rx * kx, ry: c.ry * ky })),
    tint,
  };
}

function intersect(a: UnitRect, b: UnitRect): UnitRect {
  const x0 = Math.max(a.x, b.x);
  const y0 = Math.max(a.y, b.y);
  const x1 = Math.min(a.x + a.w, b.x + b.w);
  const y1 = Math.min(a.y + a.h, b.y + b.h);
  return { x: x0, y: y0, w: Math.max(0, x1 - x0), h: Math.max(0, y1 - y0) };
}
